import { useEffect, useImperativeHandle, useLayoutEffect, useRef } from "react";
import type { OnMount } from "@monaco-editor/react";
import { LINE_HEIGHT, PADDING_Y } from "../../AiToolboxSection.constants";
import type { ChatInputProps, Monaco, MonacoEditor } from "./ChatInput.types";
import { useEntityMentions } from "./hooks/useEntityMentions";
import { useMonacoTheme } from "./hooks/useMonacoTheme";
import { useSkills } from "./hooks/useSkills";
import { getVisualLineCount } from "./utils/ChatInput.utils";
import { injectDecorationStyles } from "./utils/decorationStyles.utils";
import {
  createEntityCompletionProvider,
  setupEntityDecorations,
} from "./utils/entityMention.utils";
import {
  defineCustomThemes,
  registerCustomLanguage,
  setMonacoTheme,
} from "./utils/monacoTheme.utils";
import { setupMonacoKeyboard } from "./utils/setupMonacoKeyboard.utils";
import {
  createCompletionProvider,
  setupSkillDecorations,
} from "./utils/skill.utils";

type MonacoDisposable = { dispose: () => void };

interface UseChatInputReturn {
  handleEditorMount: OnMount;
  heightRef: React.RefObject<HTMLDivElement | null>;
}

export function useChatInput(
  onSubmit: ChatInputProps["onSubmit"],
  onCancel: ChatInputProps["onCancel"],
  ref: ChatInputProps["ref"]
): UseChatInputReturn {
  const editorRef = useRef<MonacoEditor | null>(null);
  const monacoRef = useRef<Monaco | null>(null);
  const heightRef = useRef<HTMLDivElement | null>(null);

  const disposablesRef = useRef<MonacoDisposable[]>([]);
  // Monaco's addCommand captures closures once at mount — ref indirection
  // ensures keyboard handlers always invoke the latest callbacks.
  // React Compiler cannot fix this: the closure lives in Monaco's command
  // registry, outside React's render cycle.
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(
    () => () => {
      disposablesRef.current.forEach((d) => {
        d?.dispose?.();
      });
      disposablesRef.current = [];
    },
    []
  );

  // Expose imperative handle for parent component
  useImperativeHandle(
    ref,
    () => ({
      focus: () => editorRef.current?.focus(),
      clear: () => {
        const model = editorRef.current?.getModel();
        if (!model || model.getValue() === "") return;
        // setValue('') triggers onDidChangeModelContent → onChange, syncing parent hasValue.
        model.setValue("");
      },
      openSkillSuggestions: () => {
        const editor = editorRef.current;
        const model = editor?.getModel();
        if (!editor || !model) return false;

        model.setValue("/");
        editor.setPosition({ lineNumber: 1, column: 2 });
        editor.focus();
        void editor.trigger("story", "editor.action.triggerSuggest", {});
        return true;
      },
    }),
    []
  );

  // Custom hooks for different responsibilities
  const mode = useMonacoTheme(monacoRef);
  const { skillsRef, updateDecorationsRef } = useSkills();
  const { entitiesRef, updateDecorationsRef: updateEntityDecorationsRef } =
    useEntityMentions();

  const handleEditorMount: OnMount = (ed, monaco) => {
    editorRef.current = ed;
    monacoRef.current = monaco;

    disposablesRef.current.forEach((d) => d.dispose());
    disposablesRef.current = [];

    // Inject CSS for decoration highlights (idempotent)
    injectDecorationStyles();

    // Setup themes and set initial theme
    defineCustomThemes(monaco);
    setMonacoTheme(monaco, mode);

    // Register custom language (not a disposable — languages.register returns void)
    registerCustomLanguage(monaco);

    // Setup skills decorations
    const { updateDecorations } = setupSkillDecorations(ed, monaco, skillsRef);
    updateDecorationsRef.current = updateDecorations;

    // Setup entity mention decorations
    const { updateDecorations: updateEntityDecorations } = setupEntityDecorations(
      ed,
      monaco,
      entitiesRef
    );
    updateEntityDecorationsRef.current = updateEntityDecorations;

    // Initial decoration updates
    updateDecorations();
    updateEntityDecorations();

    // Update decorations on text change
    const contentChangeDisposable = ed.onDidChangeModelContent(() => {
      updateDecorations();
      updateEntityDecorations();
    });

    // Create and register completion providers
    const completionDisposable = createCompletionProvider(monaco, skillsRef);
    const entityCompletionDisposable = createEntityCompletionProvider(
      monaco,
      entitiesRef
    );

    setupMonacoKeyboard(
      ed,
      monaco,
      () => {
        onSubmitRef.current();
      },
      () => onCancelRef.current?.(),
      () => {
        const lines = ed.getModel()?.getLineCount();

        if (lines && heightRef.current) {
          heightRef.current.style.height = `${lines}lh`;
        }
      }
    );

    disposablesRef.current = [
      contentChangeDisposable,
      completionDisposable,
      entityCompletionDisposable,
    ];
  };

  return {
    handleEditorMount,
    heightRef,
  };
}

export function useUpdateEditorHeight(
  heightRef: React.RefObject<HTMLDivElement | null>
) {
  useLayoutEffect(() => {
    const container = heightRef.current;
    if (!container) return;

    let prevLines = 0;
    const updateHeight = () => {
      const lines = getVisualLineCount(container);
      if (lines === prevLines) return;
      prevLines = lines;
      container.style.height = `${Math.max(lines, 1) * LINE_HEIGHT + PADDING_Y}px`;
    };

    updateHeight();
    const observer = new MutationObserver(updateHeight);
    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
}
